import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, AGING, PRIORITY, fmtDate } from "@/lib/format";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Search, Phone, Clock, LayoutGrid, LayoutList, Download, RotateCcw, Sparkles } from "lucide-react";
import { STAGE_IDS, ACTIVE_STAGES, TRASH_STATUS } from "@/lib/stages";

const STATUS_BADGE = {
  "New Client": "bg-violet-500/15 text-violet-400",
  "Konsultasi": "bg-blue-500/15 text-blue-400",
  "Survei": "bg-cyan-500/15 text-cyan-400",
  "SPH": "bg-amber-500/15 text-amber-400",
  "Closing": "bg-emerald-500/15 text-emerald-400",
  "Lost": "bg-red-500/15 text-red-400",
  "Tidak Jelas": "bg-zinc-500/20 text-zinc-400",
};
const FOLDERS = [["all", "Semua"], ["active", "Aktif"], [TRASH_STATUS, "Kotak Sampah"]];
const AI_SCORE_CLS = "bg-blue-500/15 text-blue-300 border-blue-500/30";

export default function Leads() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  // Desktop defaults to the table/list view (easier to scan many leads at once); mobile
  // keeps defaulting to the card view it always has — this is purely which one opens
  // first, both are still one click away via the toggle either way.
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth >= 1024 ? "table" : "card"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(searchParams.get("open"));
  // Set once when a notification deep-links straight to a tab (e.g. "diskusi", "bayar") —
  // read once rather than kept reactive to searchParams, so switching tabs by hand inside
  // the sheet afterwards doesn't get silently reset back by a stale query param.
  const [initialTab] = useState(searchParams.get("tab") || undefined);
  const [sel, setSel] = useState([]);
  const [exporting, setExporting] = useState(false);
  const readOnly = false;
  const isTrash = status === TRASH_STATUS;
  const closeDetail = () => { setSelected(null); if (searchParams.get("open") || searchParams.get("tab")) { searchParams.delete("open"); searchParams.delete("tab"); setSearchParams(searchParams, { replace: true }); } };
  React.useEffect(() => {
    const open = searchParams.get("open");
    if (open) setSelected(open);
  }, [searchParams]);

  const { data: leads = [], isLoading } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });

  const matchStatus = (l) =>
    status === "all" ? true :
    status === "active" ? ACTIVE_STAGES.includes(l.status) :
    l.status === status;
  const filtered = leads.filter((l) => matchStatus(l) && (!q || l.nama.toLowerCase().includes(q.toLowerCase()) || (l.no_hp || "").includes(q)));

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const restore = async (ids) => {
    if (!ids.length) return;
    try { const { data } = await api.post("/leads/bulk-restore", { lead_ids: ids }); toast.success(`${data.restored} data dikembalikan`); setSel([]); qc.invalidateQueries(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/leads/export/excel", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "semua_leads.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  return (
    <div className="px-4 py-5 max-w-5xl mx-auto md:max-w-none md:px-8">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-bold font-heading tracking-tight">Daftar Leads</h1>
        <div className="flex items-center gap-2">
          {user?.role !== "sales" && user?.role !== "manager" && (
            <Button data-testid="export-excel-btn" onClick={exportExcel} disabled={exporting} variant="outline" size="sm" className="h-9 bg-[#18181B] border-white/10">{exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} className="mr-1.5" />}Export Excel</Button>
          )}
          <div className="flex bg-[#18181B] rounded-lg p-1 border border-white/5">
            <button data-testid="leads-view-card" onClick={() => setView("card")} className={`p-1.5 rounded-md ${view === "card" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}><LayoutGrid size={16} /></button>
            <button data-testid="leads-view-table" onClick={() => setView("table")} className={`p-1.5 rounded-md ${view === "table" ? "bg-blue-500/20 text-blue-300" : "text-zinc-500"}`}><LayoutList size={16} /></button>
          </div>
        </div>
      </div>
      <p className="text-sm text-zinc-500 mb-3">{filtered.length} leads {user?.role === "sales" ? "milik kamu" : user?.role === "manager" ? "cabang kamu" : "total"}{user?.role !== "sales" && user?.role !== "manager" ? " · Export mencakup semua data termasuk sampah" : ""}</p>

      {/* Folder chips */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {FOLDERS.map(([v, l]) => (
          <button key={v} data-testid={`folder-${v}`} onClick={() => { setStatus(v); setSel([]); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${status === v ? "bg-blue-500/20 text-blue-300 border-blue-500/40" : "bg-[#18181B] text-zinc-400 border-white/10"}`}>{l}</button>
        ))}
      </div>

      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / no HP…" className="pl-9 bg-[#18181B] border-white/10 h-10" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setSel([]); }}>
          <SelectTrigger data-testid="leads-filter" className="w-40 bg-[#18181B] border-white/10 h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#18181B] border-white/10">
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="active">Aktif (Proses)</SelectItem>
            {STAGE_IDS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectItem value={TRASH_STATUS}>Kotak Sampah</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Trash restore bar */}
      {isTrash && !readOnly && filtered.length > 0 && (
        <div className="flex gap-2 mb-3" data-testid="trash-actions">
          <Button data-testid="restore-selected" disabled={!sel.length} onClick={() => restore(sel)} variant="outline" className="h-9 bg-[#18181B] border-white/10"><RotateCcw size={14} className="mr-1.5" />Restore Terpilih ({sel.length})</Button>
          <Button data-testid="restore-all" onClick={() => restore(filtered.map((l) => l.lead_id))} className="h-9 bg-emerald-600 hover:bg-emerald-700"><RotateCcw size={14} className="mr-1.5" />Restore Semua</Button>
        </div>
      )}

      {isLoading ? null : (
        filtered.length === 0 ? <div className="text-center text-zinc-600 py-16">{isTrash ? "Tidak ada data sampah 🎉" : "Tidak ada leads"}</div> :
        view === "table" ? (
          <div className="card-surface rounded-2xl overflow-x-auto hide-scrollbar" data-testid="leads-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-zinc-500 text-xs uppercase border-b border-white/10">
                {isTrash && !readOnly && <th className="px-3 py-2.5"></th>}
                <th className="text-left py-2.5 px-3 font-medium">Nama</th>
                <th className="text-left font-medium">No HP</th>
                <th className="text-left font-medium">Layanan</th>
                <th className="text-left font-medium">Kota</th>
                <th className="text-left font-medium">Status</th>
                <th className="text-left font-medium">Skor</th>
                <th className="text-left font-medium">Masuk</th>
                <th className="text-right font-medium px-3">Nilai (Gabungan)</th>
              </tr></thead>
              <tbody>
                {filtered.map((l) => {
                  const aging = AGING[l.aging?.level || "none"];
                  return (
                    <tr key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className={`border-b border-white/5 last:border-0 hover:bg-white/[0.02] border-l-2 ${aging.border}`}>
                      {isTrash && !readOnly && <td className="px-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`trash-check-${l.lead_id}`} checked={sel.includes(l.lead_id)} onChange={() => toggle(l.lead_id)} className="w-4 h-4 accent-blue-500" /></td>}
                      <td className="py-2.5 px-3 font-medium whitespace-nowrap cursor-pointer" onClick={() => setSelected(l.lead_id)}>{l.nama}</td>
                      <td className="text-zinc-400 whitespace-nowrap">{l.no_hp || "-"}</td>
                      <td className="text-zinc-400 whitespace-nowrap">{l.layanan || "-"}</td>
                      <td className="text-zinc-400 whitespace-nowrap">{l.kota || "-"}</td>
                      <td><span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${STATUS_BADGE[l.status] || ""}`}>{l.status}</span></td>
                      <td>{l.ai_score && <span title={`Skor prioritas AI: ${l.ai_score.tier}`} className={`text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap inline-flex items-center gap-0.5 ${AI_SCORE_CLS}`}><Sparkles size={9} />Skor {l.ai_score.score}</span>}</td>
                      <td className="text-zinc-500 whitespace-nowrap text-xs">{fmtDate(l.created_at)}</td>
                      <td className="text-right px-3 font-semibold text-emerald-400 whitespace-nowrap tabular-nums">
                        {rupiah(l.nilai_total_gabungan ?? l.nilai_deal)}
                        {l.nilai_produksi > 0 && <div className="text-[10px] font-normal text-purple-400">Produksi {rupiah(l.nilai_produksi)}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-2.5">
            {filtered.map((l) => {
              const aging = AGING[l.aging?.level || "none"];
              return (
                <div key={l.lead_id} data-testid={`lead-row-${l.lead_id}`}
                  className={`card-surface border-l-2 ${aging.border} rounded-xl p-4 hover:border-white/20 transition-colors flex items-start gap-3`}>
                  {isTrash && !readOnly && <input type="checkbox" data-testid={`trash-check-${l.lead_id}`} checked={sel.includes(l.lead_id)} onChange={() => toggle(l.lead_id)} className="w-4 h-4 accent-blue-500 mt-1" />}
                  <div className="flex items-start justify-between gap-3 flex-1 cursor-pointer" onClick={() => setSelected(l.lead_id)}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{l.nama}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${PRIORITY[l.prioritas] || ""}`}>{l.prioritas}</span>
                        {l.ai_score && <span data-testid={`score-${l.lead_id}`} title={`Skor prioritas AI: ${l.ai_score.tier}`} className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 font-medium inline-flex items-center gap-0.5 ${AI_SCORE_CLS}`}><Sparkles size={9} />Skor {l.ai_score.score}</span>}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><Phone size={11} />{l.no_hp || "-"}</span>
                        <span>· {l.layanan || l.kategori_customer || "-"}</span>
                        <span>· {l.kota || "-"}</span>
                      </div>
                      <div className="text-[11px] text-zinc-600 mt-1">Masuk {fmtDate(l.created_at)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-emerald-400 font-heading whitespace-nowrap tabular-nums">{rupiah(l.nilai_total_gabungan ?? l.nilai_deal)}</div>
                      {l.nilai_produksi > 0 && <div className="text-[10px] text-purple-400">Produksi {rupiah(l.nilai_produksi)}</div>}
                      <div className={`text-[11px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded whitespace-nowrap ${STATUS_BADGE[l.status] || ""}`}>
                        {ACTIVE_STAGES.includes(l.status) ? <><Clock size={10} />{l.aging?.days}h</> : l.status}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
      <LeadDetailSheet leadId={selected} initialTab={initialTab} onClose={closeDetail} />
    </div>
  );
}
